import { Injectable } from '@angular/core';
import { AppUpdate, AppUpdateAvailability, FlexibleUpdateInstallStatus } from '@capawesome/capacitor-app-update';
import { AlertController, Platform } from '@ionic/angular';
import { SentryService } from './sentry.service';
import { StorageService } from './storage.service';
import { StorageEntityName } from '../model/storage.model';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private updateCheckInProgress = false;
  private readonly POSTPONE_DURATION_MS = 24 * 60 * 60 * 1000;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 2000;

  constructor(
    private platform: Platform,
    private alertController: AlertController,
    private sentryService: SentryService,
    private storageService: StorageService,
  ) { }

  async checkForUpdates(): Promise<void> {
    if (!this.platform.is('capacitor')) {
      console.log('🔄 Verificación de actualizaciones solo disponible en plataforma nativa');
      return;
    }

    if (this.updateCheckInProgress) {
      return;
    }

    try {
      this.updateCheckInProgress = true;

      // Android: flexible in-app update via Play Store
      if (this.platform.is('android')) {
        const result = await AppUpdate.getAppUpdateInfo();

        console.log('📱 Info de actualización:', result);

        if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
          console.log('✅ Actualización disponible');

          if (await this.isPostponed()) {
            console.log('⏰ Actualización pospuesta, esperando 24hs');
            return;
          }

          await this.showFlexibleUpdateAlert();
        } else if (result.updateAvailability === AppUpdateAvailability.UPDATE_NOT_AVAILABLE) {
          console.log('✅ App actualizada - No hay actualizaciones disponibles');
        } else if (result.updateAvailability === AppUpdateAvailability.UPDATE_IN_PROGRESS) {
          console.log('⏳ Actualización en progreso');
        }
      }

      // iOS: verificar versión y redirigir al App Store
      if (this.platform.is('ios')) {
        try {
          const result = await AppUpdate.getAppUpdateInfo();
          if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
            await this.showIosUpdateAlert();
          }
        } catch (iosError) {
          console.warn('iOS update check falló:', iosError);
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Install Error(-9)') ||
          errorMessage.includes('ERROR_PLAY_STORE_NOT_FOUND')) {
        return;
      }
      console.error('❌ Error al verificar actualizaciones:', error);
      this.sentryService.captureException(error, {
        tags: { feature: 'app-update' },
        extra: { action: 'checkForUpdates' }
      });
    } finally {
      this.updateCheckInProgress = false;
    }
  }

  private async showIosUpdateAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Actualización Disponible',
      message: 'Hay una nueva versión disponible en el App Store.',
      buttons: [
        {
          text: 'Más Tarde',
          role: 'cancel',
        },
        {
          text: 'Actualizar',
          handler: () => {
            AppUpdate.openAppStore();
          },
        },
      ],
    });
    await alert.present();
  }

  private async isPostponed(): Promise<boolean> {
    const postponedAt = await this.storageService.get<string>(StorageEntityName.updatePostponedAt);
    if (!postponedAt) {
      return false;
    }
    const elapsed = Date.now() - Number(postponedAt);
    return elapsed < this.POSTPONE_DURATION_MS;
  }

  private async showFlexibleUpdateAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Actualización Disponible',
      message: 'Hay una nueva versión disponible. ¿Deseas actualizar ahora?',
      buttons: [
        {
          text: 'Más Tarde',
          role: 'cancel',
          cssClass: 'secondary',
          handler: async () => {
            await this.storageService.set(StorageEntityName.updatePostponedAt, Date.now().toString());
          }
        },
        {
          text: 'Actualizar',
          handler: () => {
            this.performFlexibleUpdate(1);
          }
        }
      ]
    });

    await alert.present();
  }

  private async performFlexibleUpdate(attempt: number): Promise<void> {
    const loading = await this.alertController.create({
      message: 'Descargando actualización...',
      backdropDismiss: false
    });

    await loading.present();

    try {
      console.log(`🚀 Iniciando actualización flexible (intento ${attempt}/${this.MAX_RETRIES})...`);

      await AppUpdate.startFlexibleUpdate();

      await AppUpdate.addListener('onFlexibleUpdateStateChange', async (state) => {
        console.log('📥 Estado de actualización:', state);

        if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
          await loading.dismiss();
          this.showUpdateReadyAlert();
        } else if (state.installStatus === FlexibleUpdateInstallStatus.FAILED) {
          await loading.dismiss();
          if (attempt < this.MAX_RETRIES) {
            this.performFlexibleUpdate(attempt + 1);
          } else {
            this.openPlayStore();
          }
        }
      });

    } catch (error) {
      await loading.dismiss();
      console.error(`❌ Error en actualización flexible (intento ${attempt}):`, error);

      if (attempt < this.MAX_RETRIES) {
        await this.delay(this.RETRY_DELAY_MS * attempt);
        this.performFlexibleUpdate(attempt + 1);
      } else {
        this.sentryService.captureException(error, {
          tags: { feature: 'app-update' },
          extra: { action: 'performFlexibleUpdate', attempt, finalAction: 'openPlayStore' }
        });
        this.openPlayStore();
      }
    }
  }

  private async showUpdateReadyAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Actualización Lista',
      message: 'La actualización se ha descargado. ¿Deseas instalarla ahora? La app se reiniciará.',
      buttons: [
        {
          text: 'Más Tarde',
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: 'Instalar',
          handler: () => {
            this.completeFlexibleUpdate();
          }
        }
      ]
    });

    await alert.present();
  }

  private async completeFlexibleUpdate(): Promise<void> {
    try {
      console.log('✅ Completando actualización flexible...');
      await AppUpdate.completeFlexibleUpdate();
    } catch (error) {
      console.error('❌ Error al completar actualización:', error);
      this.sentryService.captureException(error, {
        tags: { feature: 'app-update' },
        extra: { action: 'completeFlexibleUpdate' }
      });
    }
  }

  private async openPlayStore(): Promise<void> {
    try {
      await AppUpdate.openAppStore();
    } catch (error) {
      console.error('❌ Error al abrir Play Store:', error);
      this.sentryService.captureException(error, {
        tags: { feature: 'app-update' },
        extra: { action: 'openPlayStore' }
      });
    }
  }

  async manualCheckForUpdates(): Promise<void> {
    const loading = await this.alertController.create({
      message: 'Verificando actualizaciones...',
      backdropDismiss: false
    });

    await loading.present();

    try {
      const result = await AppUpdate.getAppUpdateInfo();

      if (result.updateAvailability === AppUpdateAvailability.UPDATE_NOT_AVAILABLE) {
        await loading.dismiss();

        const alert = await this.alertController.create({
          header: 'App Actualizada',
          message: 'Ya tienes la última versión instalada.',
          buttons: ['OK']
        });
        await alert.present();
      } else if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        await loading.dismiss();
        await this.showFlexibleUpdateAlert();
      } else {
        await loading.dismiss();
      }
    } catch (error) {
      await loading.dismiss();

      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Install Error(-9)') ||
          errorMessage.includes('ERROR_PLAY_STORE_NOT_FOUND')) {
        return;
      }

      const alert = await this.alertController.create({
        header: 'Error',
        message: 'No se pudo verificar actualizaciones. Intenta más tarde.',
        buttons: ['OK']
      });
      await alert.present();
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
